import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'

import { canView, isPublicPath, landingPath } from '../lib/access'
import type { Me } from '../lib/auth'
import { FORBIDDEN_EVENT, UNAUTHORIZED_EVENT, type ForbiddenEventDetail } from '../lib/http'
import { useAccess, useSession } from '../lib/session'
import {
  ForcedPasswordScreen,
  LoginScreen,
  NoAccess,
  ReauthDialog,
  SessionErrorScreen,
  Toast,
  type ToastMessage,
} from './auth/AccessScreens'
import { SessionProvider } from './auth/SessionProvider'
import { UserMenu } from './auth/UserMenu'
import { CLUB_NAME, OUR_TEAM_ID } from '../lib/club'
import { HubWordmark } from './HubWordmark'
import { TeamCrest } from './TeamCrest'

interface TabDef {
  to: string
  label: string
  // Skrót na węższych ekranach (< 1536 px): przy 1280 px pełne nazwy wszystkich
  // zakładek + konto nie mieszczą się w jednej belce. Pełna nazwa zostaje w podpowiedzi.
  short?: string
  disabled?: boolean
  // active gdy bieżąca ścieżka == lub zaczyna się od któregokolwiek z prefiksów.
  // Zakładki-parasole (Analiza przeciwnika, Trening) skupiają kilka podzakładek.
  prefixes: string[]
  // Podzakładki w kolejności paska — przy włączonej fladze zakładka jest widoczna,
  // gdy konto widzi którąkolwiek z nich, i prowadzi do pierwszej widocznej
  // (sekcje stron: lib/access.ts).
  entries: string[]
  // Strona za logowaniem NIEZALEŻNIE od flagi AUTH_REQUIRED (Squad planner) — zakładka
  // tylko z prawdziwym dostępem konta, także lokalnie przy wyłączonej fladze.
  alwaysGated?: boolean
}

const TABS: TabDef[] = [
  { to: '/raporty', label: 'Raporty meczowe', prefixes: ['/raporty'], entries: ['/raporty'] },
  {
    to: '/dossier', label: 'Analiza przeciwnika', prefixes: ['/dossier', '/gracze'],
    entries: ['/dossier', '/gracze'],
  },
  { to: '/motoryka', label: 'Motoryka', prefixes: ['/motoryka'], entries: ['/motoryka'] },
  { to: '/kadra', label: 'Kadra', prefixes: ['/kadra'], entries: ['/kadra'] },
]

export function AppShell() {
  return (
    <SessionProvider>
      <Shell />
    </SessionProvider>
  )
}

// Bramka logowania + nawigacja wg dostępu działają TYLKO przy włączonej fladze
// AUTH_REQUIRED (GET /api/auth/config). Przy wyłączonej powłoka wygląda i działa
// jak dawniej; jedyny dodatek to menu konta, gdy ktoś jest zalogowany.
function Shell() {
  const { pathname } = useLocation()
  const { me, loading, error, setMe, reload, config, authRequired } = useSession()
  const access = useAccess()

  // ── 401 / 403 z API (lib/http.ts) — słuchamy tylko przy włączonej fladze ──
  const [reauth, setReauth] = useState(false)
  const [toast, setToast] = useState<ToastMessage | null>(null)
  const closeToast = useCallback(() => setToast(null), [])
  const closeReauth = useCallback(() => setReauth(false), [])
  // Okno ponownego logowania ma sens tylko, gdy sesja BYŁA (wygasła w trakcie
  // pracy). 401 spóźnionego zapytania po świadomym wylogowaniu go nie otwiera.
  const hadSession = useRef(false)
  useEffect(() => { hadSession.current = !!me }, [me])

  useEffect(() => {
    if (!authRequired) return
    const onUnauthorized = () => { if (hadSession.current) setReauth(true) }
    const onForbidden = (e: Event) => {
      const { detail } = (e as CustomEvent<ForbiddenEventDetail>).detail
      setToast({ id: Date.now(), tone: 'warn', text: detail })
    }
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
    window.addEventListener(FORBIDDEN_EVENT, onForbidden)
    return () => {
      window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
      window.removeEventListener(FORBIDDEN_EVENT, onForbidden)
    }
  }, [authRequired])

  const onLoggedIn = useCallback((m: Me) => { setReauth(false); setMe(m) }, [setMe])
  const onReauthed = useCallback((m: Me) => {
    setReauth(false)
    setMe(m)
    setToast({ id: Date.now(), tone: 'ok', text: 'Zalogowano — powtórz ostatnią akcję.' })
  }, [setMe])
  const onLoggedOut = useCallback(() => { setReauth(false); setMe(null) }, [setMe])

  // ── Bramka ──
  // Do czasu odpowiedzi /config nie wiadomo, czy wpuszczać — treść i zakładki
  // czekają (jedno małe zapytanie, równolegle z /me); przy fladze czekamy też na /me.
  const pending = config === null || (authRequired && loading)
  const gated = authRequired && !isPublicPath(pathname)
  if (!pending && gated) {
    if (!me && error) return <SessionErrorScreen error={error} onRetry={reload} />
    if (!me) return <LoginScreen onLoggedIn={onLoggedIn} />
    if (me.user.must_change_password) {
      return <ForcedPasswordScreen me={me} onChanged={reload} onLoggedOut={onLoggedOut} />
    }
  }
  const allowed = !gated || canView(me, pathname)

  const tabs = TABS.flatMap((tab) => {
    if (tab.alwaysGated) return canView(me, tab.to) ? [{ tab, to: tab.to }] : []
    if (!access.enforced) return [{ tab, to: tab.to }]
    const first = tab.entries.find((p) => access.canView(p))
    return first ? [{ tab, to: first }] : []
  })
  // Przy włączonej fladze bez sesji (np. publiczne /konto) — sama belka, bez zakładek.
  const showNav = !pending && (!authRequired || !!me)

  return (
    <div className="flex min-h-full flex-col">
      {/* Belka aplikacji: znak, zakładki-pigułki i konto w jednym ciemnym pasku
          na całą szerokość. */}
      <header className="no-print sticky top-0 z-30 bg-forest-800 text-white">
        <div className="flex h-[60px] items-center gap-6 px-7">
          <Link to="/" className="flex flex-none items-center gap-2.5" title="Strona startowa">
            <TeamCrest teamId={OUR_TEAM_ID} teamName={CLUB_NAME} size={30} onDark />
            <HubWordmark className="text-sm" />
          </Link>

          <nav className="min-w-0 flex-1">
            <ul className="flex gap-1 overflow-x-auto text-[13px] font-semibold [scrollbar-width:none]">
              {/* Pusty pasek trzyma wysokość, póki nie wiadomo, które zakładki pokazać. */}
              {!showNav && <li aria-hidden className="invisible px-3 py-2">&nbsp;</li>}
              {showNav && tabs.map(({ tab, to }) => {
                const isActive = tab.prefixes.some(
                  (p) => pathname === p || pathname.startsWith(p + '/'),
                )

                if (tab.disabled) {
                  return (
                    <li key={tab.to}>
                      <span className="inline-flex cursor-not-allowed items-center gap-2 whitespace-nowrap px-3 py-2 text-white/35" title="wkrótce">
                        {tab.label}
                      </span>
                    </li>
                  )
                }
                return (
                  <li key={tab.to}>
                    <NavLink
                      to={to}
                      title={tab.short ? tab.label : undefined}
                      className={[
                        'inline-flex items-center whitespace-nowrap rounded-lg px-3 py-2 transition-colors',
                        isActive ? 'bg-white/[0.12] text-white' : 'text-white/70 hover:bg-white/[0.06] hover:text-white',
                      ].join(' ')}
                    >
                      {tab.short ? (
                        <>
                          <span className="hidden 2xl:inline">{tab.label}</span>
                          <span className="2xl:hidden">{tab.short}</span>
                        </>
                      ) : tab.label}
                    </NavLink>
                  </li>
                )
              })}
            </ul>
          </nav>

          <div className="flex flex-none items-center gap-3">
            {me && <UserMenu me={me} onLoggedOut={onLoggedOut} />}
          </div>
        </div>
      </header>

      <main className="flex-1">
        {pending ? null : allowed ? <Outlet /> : <NoAccess landing={landingPath(me)} />}
      </main>

      {authRequired && reauth && me && <ReauthDialog onLoggedIn={onReauthed} onClose={closeReauth} />}
      {authRequired && <Toast toast={toast} onClose={closeToast} />}
    </div>
  )
}
